import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { format, parseISO, addDays, differenceInDays } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import RichTextEditor from './RichTextEditor';

const TasksSection = ({ trainerId, selectedPlan, onRefresh, highlightTaskId, refreshKey, onPreviewRequest }) => {
  const [tasks, setTasks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPlan, setFilterPlan] = useState(selectedPlan?.id || '');
  const [currentPlanData, setCurrentPlanData] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState(new Set([1]));
  const [isSyncing, setIsSyncing] = useState(false);
  const [quickUpdatingField, setQuickUpdatingField] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const taskRefs = useRef({});

  const initialFormState = {
    plan_id: '',
    title: '',
    description: '',
    week_number: 1,
    day_number: 1,
    task_date: '',
    demo_videos: [''],
    demo_video_title: '',
    resource_url: '',
    task_checkboxes: [''],
    require_video_submission: false,
    video_submission_description: '',
    lesson_title: '',
    lesson_content: '',
    lesson_collapsed: true,
    is_rest_day: false
  };

  const [formData, setFormData] = useState(initialFormState);

  const calculateDateFromPosition = useCallback((week, day) => {
    if (!currentPlanData?.start_date) return null;
    try {
      const [year, month, day_part] = currentPlanData.start_date.split('-').map(Number);
      const baseDate = new Date(year, month - 1, day_part);
      const daysToAdd = ((week || 1) - 1) * 7 + ((day || 1) - 1);
      return format(addDays(baseDate, daysToAdd), 'yyyy-MM-dd');
    } catch (e) {
      return null;
    }
  }, [currentPlanData]);

  const getDayLabel = useCallback((week, day) => {
    const dateStr = calculateDateFromPosition(week, day);
    if (!dateStr) return `Day ${day}`;
    return format(parseISO(dateStr), 'EEEE, MMM do');
  }, [calculateDateFromPosition]);

  const fetchTasks = useCallback(async () => {
    if (!filterPlan) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: planData } = await supabase
        .from('training_plans')
        .select('*, training_clients(*)')
        .eq('id', filterPlan)
        .single();
      
      setCurrentPlanData(planData);

      const { data, error } = await supabase
        .from('training_tasks')
        .select('*')
        .eq('plan_id', filterPlan)
        .order('week_number', { ascending: true })
        .order('day_number', { ascending: true });

      if (error) throw error;
      setTasks(data || []);

      if (highlightTaskId) {
        const target = data.find(t => t.id === highlightTaskId);
        if (target) {
          setExpandedWeeks(prev => new Set([...prev, target.week_number]));
          setTimeout(() => {
            taskRefs.current[highlightTaskId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 500);
        }
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [filterPlan, highlightTaskId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, refreshKey]);

  useEffect(() => {
    const loadData = async () => {
      const { data: cl } = await supabase.from('training_clients').select('id').eq('trainer_id', trainerId);
      const ids = cl?.map(c => c.id) || [];
      if (ids.length > 0) {
        const { data: p } = await supabase.from('training_plans').select('id, plan_name, training_clients(dog_name)').in('client_id', ids).order('plan_name');
        setPlans(p || []);
      }
      const { data: t } = await supabase.from('lesson_templates_20250218').select('*').eq('trainer_id', trainerId).order('title');
      setTemplates(t || []);
    };
    loadData();
  }, [trainerId]);

  const handleRepairSchedule = async () => {
    if (!currentPlanData?.start_date || !tasks.length) return;
    if (!window.confirm('This will completely re-sequence all tasks to ensure exactly one task per day, starting from the plan start date. Continue?')) return;
    
    setIsSyncing(true);
    try {
      const sortedTasks = [...tasks].sort((a, b) => {
        if (a.week_number !== b.week_number) return (a.week_number || 1) - (b.week_number || 1);
        if (a.day_number !== b.day_number) return (a.day_number || 1) - (b.day_number || 1);
        return String(a.id).localeCompare(String(b.id));
      });

      const [year, month, day_part] = currentPlanData.start_date.split('-').map(Number);
      const baseDate = new Date(year, month - 1, day_part);

      const updates = sortedTasks.map((task, index) => {
        const newWeek = Math.floor(index / 7) + 1;
        const newDay = (index % 7) + 1;
        const taskDate = format(addDays(baseDate, index), 'yyyy-MM-dd');
        return {
          id: task.id,
          week_number: newWeek,
          day_number: newDay,
          task_date: taskDate
        };
      });

      const promises = updates.map(update => 
        supabase.from('training_tasks').update({
          week_number: update.week_number,
          day_number: update.day_number,
          task_date: update.task_date
        }).eq('id', update.id)
      );

      await Promise.all(promises);
      await fetchTasks();
      if (onRefresh) onRefresh();
      alert('Schedule repaired!');
    } catch (err) {
      console.error(err);
      alert('Failed to repair schedule.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleQuickUpdate = async (taskId, field, value) => {
    setQuickUpdatingField({ id: taskId, field });
    try {
      const task = tasks.find(t => t.id === taskId);
      const updates = [];

      if (field === 'day_number') {
        const conflictingTask = tasks.find(t => t.week_number === task.week_number && t.day_number === value && t.id !== taskId);
        if (conflictingTask) {
          updates.push({ id: conflictingTask.id, day_number: task.day_number, task_date: calculateDateFromPosition(task.week_number, task.day_number) });
        }
        updates.push({ id: taskId, day_number: value, task_date: calculateDateFromPosition(task.week_number, value) });
      } else if (field === 'week_number') {
        const weekTasks = tasks.filter(t => t.week_number === value && t.id !== taskId);
        let nextDay = 1;
        while (weekTasks.some(t => t.day_number === nextDay)) nextDay++;
        if (nextDay > 7) {
          alert("Week full!");
          return;
        }
        updates.push({ id: taskId, week_number: value, day_number: nextDay, task_date: calculateDateFromPosition(value, nextDay) });
      } else if (field === 'is_rest_day') {
        updates.push({ id: taskId, is_rest_day: value });
      }

      for (const update of updates) {
        const { id, ...data } = update;
        await supabase.from('training_tasks').update(data).eq('id', id);
      }

      const { data: allTasks } = await supabase.from('training_tasks').select('id, week_number, day_number').eq('plan_id', task.plan_id);
      if (allTasks) {
        const resyncPromises = allTasks.map(t => 
          supabase.from('training_tasks').update({ task_date: calculateDateFromPosition(t.week_number, t.day_number) }).eq('id', t.id)
        );
        await Promise.all(resyncPromises);
      }

      await fetchTasks();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setQuickUpdatingField(null);
    }
  };

  const handleSyncDates = async (newStartDate) => {
    if (!newStartDate || !tasks.length) return;
    setIsSyncing(true);
    try {
      await supabase.from('training_plans').update({ start_date: newStartDate }).eq('id', filterPlan);
      
      const [year, month, day] = newStartDate.split('-').map(Number);
      const baseDate = new Date(year, month - 1, day);

      const updates = tasks.map(task => ({
        id: task.id,
        task_date: format(addDays(baseDate, ((task.week_number || 1) - 1) * 7 + ((task.day_number || 1) - 1)), 'yyyy-MM-dd')
      }));

      const promises = updates.map(update => 
        supabase.from('training_tasks').update({ task_date: update.task_date }).eq('id', update.id)
      );

      await Promise.all(promises);
      await fetchTasks();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure?')) return;
    setDeletingId(taskId);
    try {
      await supabase.from('training_tasks').delete().eq('id', taskId);
      fetchTasks();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleApplyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      title: template.title || prev.title,
      description: template.description || prev.description,
      lesson_title: template.lesson_title || template.title || prev.lesson_title,
      lesson_content: template.content || prev.lesson_content,
      lesson_collapsed: template.lesson_collapsed !== undefined ? template.lesson_collapsed : true,
      demo_videos: template.demo_videos?.length ? template.demo_videos : [''],
      demo_video_title: template.demo_video_title || prev.demo_video_title,
      task_checkboxes: template.task_checkboxes?.length ? template.task_checkboxes : [''],
      require_video_submission: template.require_video_submission || false,
      video_submission_description: template.video_submission_description || prev.video_submission_description,
      resource_url: template.resource_url || prev.resource_url,
      is_rest_day: !!template.is_rest_day
    }));
    setShowTemplatePicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setFormError('');
    setIsSubmitting(true);

    try {
      const finalPlanId = formData.plan_id || filterPlan;
      if (!finalPlanId) throw new Error('No plan selected.');

      const isSlotTaken = tasks.find(t => 
        t.week_number === parseInt(formData.week_number) && 
        t.day_number === parseInt(formData.day_number) && 
        (!editingTask || t.id !== editingTask.id)
      );

      if (isSlotTaken) {
        throw new Error(`A task ("${isSlotTaken.title}") already exists for Week ${formData.week_number}, Day ${formData.day_number}. Please choose a different day or move the existing task first.`);
      }

      const submitData = {
        plan_id: finalPlanId,
        title: formData.title.trim(),
        description: formData.description?.trim() || null,
        week_number: parseInt(formData.week_number) || 1,
        day_number: parseInt(formData.day_number) || 1,
        task_date: calculateDateFromPosition(formData.week_number, formData.day_number),
        demo_videos: (formData.demo_videos || []).filter(v => v && v.trim()),
        demo_video_title: formData.demo_video_title?.trim() || null,
        resource_url: formData.resource_url?.trim() || null,
        task_checkboxes: (formData.task_checkboxes || []).filter(v => v && v.trim()),
        require_video_submission: formData.require_video_submission || false,
        video_submission_description: formData.video_submission_description || null,
        lesson_title: formData.lesson_title?.trim() || null,
        lesson_content: formData.lesson_content || null,
        lesson_collapsed: formData.lesson_collapsed !== undefined ? formData.lesson_collapsed : true,
        is_rest_day: formData.is_rest_day || false
      };

      if (editingTask) {
        await supabase.from('training_tasks').update(submitData).eq('id', editingTask.id);
      } else {
        await supabase.from('training_tasks').insert([submitData]);
      }

      setShowCreateForm(false);
      setEditingTask(null);
      await fetchTasks();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tasksByWeek = tasks.reduce((acc, t) => {
    const w = t.week_number || 1;
    (acc[w] = acc[w] || []).push(t);
    return acc;
  }, {});

  const hasOverlap = useMemo(() => {
    const slots = tasks.map(t => `${t.week_number}-${t.day_number}`);
    const dates = tasks.filter(t => t.task_date).map(t => t.task_date);
    return new Set(slots).size !== tasks.length || new Set(dates).size !== dates.length;
  }, [tasks]);

  return (
    <div className="space-y-6">
      <div className="bg-indigo-600 rounded-[2.5rem] p-8 shadow-xl shadow-indigo-100 text-white relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-md">
              <SafeIcon name="Calendar" className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-black">Sequential Timeline</h3>
              <p className="text-indigo-100 text-sm font-medium">Sync all tasks to a specific start date to maintain order.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasOverlap && filterPlan && (
              <button 
                onClick={handleRepairSchedule}
                disabled={isSyncing}
                className="bg-yellow-400 text-indigo-900 px-6 py-3 rounded-2xl font-black text-xs flex items-center gap-2 hover:bg-yellow-300 transition-all shadow-lg animate-pulse"
              >
                {isSyncing ? <div className="w-4 h-4 border-2 border-indigo-900 border-t-transparent rounded-full animate-spin" /> : <SafeIcon name="AlertTriangle" />}
                Repair Duplicates
              </button>
            )}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2 flex items-center gap-2 border border-white/20">
              <span className="text-[10px] font-black uppercase tracking-widest pl-4 text-indigo-50">Plan Start</span>
              <input 
                type="date" 
                value={currentPlanData?.start_date || ''}
                onChange={(e) => handleSyncDates(e.target.value)}
                disabled={isSyncing || !filterPlan}
                className="bg-white text-indigo-900 font-black px-4 py-2.5 rounded-xl border-none focus:ring-2 focus:ring-indigo-300 outline-none text-sm cursor-pointer"
              />
            </div>
          </div>
        </div>
        <SafeIcon name="Award" className="absolute -right-10 -bottom-10 w-48 h-48 opacity-10 rotate-12" />
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-50 rounded-xl"><SafeIcon name="Users" className="text-gray-400" /></div>
          <select 
            value={filterPlan} 
            onChange={e => setFilterPlan(e.target.value)}
            className="border-none bg-transparent font-black text-gray-900 p-1 rounded-xl outline-none cursor-pointer"
          >
            <option value="">Select Training Plan...</option>
            {plans.map(p => <option key={p.id} value={p.id}>{p.training_clients?.dog_name} - {p.plan_name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {filterPlan && (
            <button 
              onClick={() => onPreviewRequest?.(currentPlanData, tasks, currentPlanData.training_clients)}
              className="bg-white border-2 border-indigo-100 text-indigo-600 px-6 py-3 rounded-2xl font-black flex items-center gap-2 hover:bg-indigo-50 transition-all active:scale-95"
            >
              <SafeIcon name="Search" /> Preview Portal
            </button>
          )}
          <button 
            onClick={() => {
              if (!filterPlan) { alert('Please select a training plan first.'); return; }
              setEditingTask(null);
              setFormData({...initialFormState, plan_id: filterPlan});
              setShowCreateForm(true);
            }} 
            className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black flex items-center gap-2 hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95"
          >
            <SafeIcon name="Plus" /> Add Task
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {Object.keys(tasksByWeek).sort((a, b) => Number(a) - Number(b)).map(week => (
          <div key={week} className="bg-white rounded-[2rem] border border-gray-100 overflow-hidden shadow-sm">
            <button 
              onClick={() => {
                const n = new Set(expandedWeeks);
                n.has(Number(week)) ? n.delete(Number(week)) : n.add(Number(week));
                setExpandedWeeks(n);
              }}
              className="w-full p-6 bg-gray-50/50 flex justify-between items-center group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center font-black text-indigo-600 border border-gray-100">
                  {week}
                </div>
                <div className="text-left">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Training Phase</span>
                  <span className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors">Week {week} Schedule</span>
                </div>
              </div>
              <SafeIcon name={expandedWeeks.has(Number(week)) ? "ChevronUp" : "ChevronDown"} className="text-gray-400 group-hover:text-indigo-600 transition-all" />
            </button>

            <AnimatePresence>
              {expandedWeeks.has(Number(week)) && (
                <motion.div 
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden bg-white"
                >
                  <div className="divide-y divide-gray-50">
                    {tasksByWeek[week].sort((a, b) => a.day_number - b.day_number).map(task => (
                      <div 
                        key={task.id} 
                        ref={el => taskRefs.current[task.id] = el}
                        className={`p-6 transition-all ${highlightTaskId === task.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : 'hover:bg-gray-50/30'}`}
                      >
                        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-6">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-black text-lg text-gray-900 leading-tight">{task.title}</h4>
                              {task.is_rest_day && (
                                <span className="bg-green-100 text-green-700 text-[10px] font-black uppercase px-2.5 py-1 rounded-lg flex items-center gap-1.5">
                                  <SafeIcon name="Clock" className="w-3.5 h-3.5" /> Rest Day
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="relative group">
                                <div className={`flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer group-hover:bg-indigo-50 group-hover:text-indigo-600 ${quickUpdatingField?.id === task.id && quickUpdatingField?.field === 'day_number' ? 'animate-pulse bg-indigo-100' : ''}`}>
                                  <SafeIcon name="Calendar" className="w-3.5 h-3.5" /> {getDayLabel(task.week_number, task.day_number)}
                                  <SafeIcon name="ChevronDown" className="w-3 h-3 opacity-50" />
                                </div>
                                <select 
                                  className="absolute inset-0 opacity-0 cursor-pointer"
                                  value={task.day_number}
                                  onChange={(e) => handleQuickUpdate(task.id, 'day_number', parseInt(e.target.value))}
                                >
                                  {[1, 2, 3, 4, 5, 6, 7].map(d => (
                                    <option key={d} value={d}>Day {d} - {getDayLabel(task.week_number, d)}</option>
                                  ))}
                                </select>
                              </div>

                              <button 
                                onClick={() => handleQuickUpdate(task.id, 'is_rest_day', !task.is_rest_day)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${task.is_rest_day ? 'bg-green-100 text-green-700' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                              >
                                <SafeIcon name="Clock" className="w-3.5 h-3.5" /> {task.is_rest_day ? 'Resting' : 'Training'}
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => {
                              setEditingTask(task);
                              setFormData({
                                ...task,
                                demo_videos: task.demo_videos?.length ? task.demo_videos : [''],
                                task_checkboxes: task.task_checkboxes?.length ? task.task_checkboxes : ['']
                              });
                              setShowCreateForm(true);
                            }} className="p-3.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-2xl transition-all"><SafeIcon name="Edit" className="w-5 h-5" /></button>
                            <button onClick={() => handleDeleteTask(task.id)} disabled={deletingId === task.id} className="p-3.5 text-gray-400 hover:text-red-50 hover:bg-red-50 rounded-2xl transition-all">
                              {deletingId === task.id ? <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <SafeIcon name="Trash2" className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showCreateForm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[9999]"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center font-black text-xl shadow-lg shadow-indigo-100">
                    {formData.day_number}
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-gray-900">{editingTask ? 'Modify Task' : 'New Task Entry'}</h3>
                    <p className="text-sm text-gray-500 font-medium">Week {formData.week_number} • {getDayLabel(formData.week_number, formData.day_number)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setShowTemplatePicker(!showTemplatePicker)}
                    className="bg-indigo-50 text-indigo-700 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-100 transition-all"
                  >
                    <SafeIcon name="Book" /> Quick Templates
                  </button>
                  <button onClick={() => setShowCreateForm(false)} className="p-4 hover:bg-gray-100 rounded-2xl transition-colors"><SafeIcon name="X" className="w-6 h-6 text-gray-400" /></button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-10">
                {formError && (
                  <div className="p-5 rounded-3xl bg-red-50 border-2 border-red-100 text-red-600 font-bold flex items-start gap-4">
                    <SafeIcon name="AlertTriangle" className="w-6 h-6 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm">Scheduling Conflict</p>
                      <p className="text-xs opacity-80 mt-1">{formError}</p>
                    </div>
                  </div>
                )}

                <section className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Target Slot</h4>
                    <label className="flex items-center gap-3 cursor-pointer group bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
                      <input 
                        type="checkbox" 
                        checked={formData.is_rest_day} 
                        onChange={e => setFormData({...formData, is_rest_day: e.target.checked})}
                        className="w-5 h-5 rounded-lg text-indigo-600 border-gray-300"
                      />
                      <span className="text-xs font-black text-gray-900 uppercase tracking-widest group-hover:text-indigo-600 transition-colors">Mark as Rest Day</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Assign Week</label>
                      <input 
                        type="number" 
                        min="1"
                        value={formData.week_number}
                        onChange={e => setFormData({...formData, week_number: parseInt(e.target.value) || 1})}
                        className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl font-black focus:border-indigo-500 transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Assign Day (1-7)</label>
                      <select 
                        value={formData.day_number}
                        onChange={e => setFormData({...formData, day_number: parseInt(e.target.value) || 1})}
                        className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl font-black focus:border-indigo-500 transition-all outline-none"
                      >
                        {[1, 2, 3, 4, 5, 6, 7].map(d => <option key={d} value={d}>Day {d} ({getDayLabel(formData.week_number, d)})</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Task Heading</label>
                    <input 
                      required
                      placeholder="e.g. Loose Lead Walking Intro"
                      value={formData.title}
                      onChange={e => setFormData({...formData, title: e.target.value})}
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 outline-none font-black text-lg"
                    />
                  </div>

                  {/* Restored Description Field */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Short Description / Instructions</label>
                    <textarea 
                      placeholder="Briefly explain the goal or instructions for this task..." 
                      value={formData.description || ''} 
                      onChange={e => setFormData({...formData, description: e.target.value})} 
                      rows={3}
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 outline-none font-medium resize-none transition-all text-gray-700" 
                    />
                  </div>
                </section>

                {!formData.is_rest_day && (
                  <>
                    {/* LESSON CONTENT */}
                    <section className="space-y-4 pt-8 border-t border-gray-100">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-xl shadow-sm"><SafeIcon name="Book" className="w-5 h-5" /></div>
                        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Training Curricula</h4>
                      </div>
                      <input 
                        placeholder="Lesson Heading (e.g. Theoretical Foundations)"
                        value={formData.lesson_title}
                        onChange={e => setFormData({...formData, lesson_title: e.target.value})}
                        className="w-full px-5 py-4 bg-gray-50 rounded-2xl font-black border-2 border-transparent focus:border-indigo-500 outline-none"
                      />
                      <RichTextEditor 
                        value={formData.lesson_content} 
                        onChange={val => setFormData({...formData, lesson_content: val})} 
                      />
                    </section>

                    {/* DEMO VIDEOS */}
                    <section className="space-y-4 pt-8 border-t border-gray-100">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-purple-50 text-purple-600 rounded-xl shadow-sm"><SafeIcon name="Video" className="w-5 h-5" /></div>
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Demo Videos</h4>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => setFormData({...formData, demo_videos: [...formData.demo_videos, '']})}
                          className="text-xs font-black text-indigo-600 hover:underline"
                        >+ Add Video</button>
                      </div>
                      
                      <input 
                        placeholder="Video Section Title (e.g. Demonstration Videos)"
                        value={formData.demo_video_title}
                        onChange={e => setFormData({...formData, demo_video_title: e.target.value})}
                        className="w-full p-4 bg-gray-50 rounded-2xl font-bold border-2 border-transparent focus:border-indigo-500 outline-none"
                      />

                      <div className="space-y-3">
                        {formData.demo_videos.map((url, i) => (
                          <div key={i} className="flex gap-2">
                            <input 
                              placeholder="Paste video link"
                              value={url}
                              onChange={e => {
                                const n = [...formData.demo_videos];
                                n[i] = e.target.value;
                                setFormData({...formData, demo_videos: n});
                              }}
                              className="flex-1 px-4 py-3 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-medium transition-all"
                            />
                            <button 
                              type="button"
                              onClick={() => setFormData({...formData, demo_videos: formData.demo_videos.filter((_, idx) => idx !== i)})}
                              className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                            ><SafeIcon name="Trash2" /></button>
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* CHECKPOINT CHECKLIST */}
                    <section className="space-y-4 pt-8 border-t border-gray-100">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="p-2 bg-green-50 text-green-600 rounded-xl shadow-sm"><SafeIcon name="CheckSquare" className="w-5 h-5" /></div>
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Owner Checklist</h4>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => setFormData({...formData, task_checkboxes: [...formData.task_checkboxes, '']})}
                          className="text-xs font-black text-indigo-600 hover:underline"
                        >+ Add Step</button>
                      </div>
                      <div className="space-y-3">
                        {formData.task_checkboxes.map((cb, i) => (
                          <div key={i} className="flex gap-2">
                            <input 
                              placeholder={`Step ${i + 1}`}
                              value={cb}
                              onChange={e => {
                                const n = [...formData.task_checkboxes];
                                n[i] = e.target.value;
                                setFormData({...formData, task_checkboxes: n});
                              }}
                              className="flex-1 p-3 bg-gray-50 rounded-xl border-2 border-transparent focus:border-indigo-500 outline-none font-medium"
                            />
                            <button 
                              type="button"
                              onClick={() => setFormData({...formData, task_checkboxes: formData.task_checkboxes.filter((_, idx) => idx !== i)})}
                              className="p-3 text-gray-400 hover:text-red-500 transition-colors"
                            ><SafeIcon name="Trash2" /></button>
                          </div>
                        ))}
                      </div>
                    </section>

                    {/* EXTERNAL LINK */}
                    <section className="space-y-4 pt-8 border-t border-gray-100">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl shadow-sm"><SafeIcon name="Link" className="w-5 h-5" /></div>
                        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">External Guide (PDF/Blog)</h4>
                      </div>
                      <input 
                        placeholder="https://..."
                        value={formData.resource_url}
                        onChange={e => setFormData({...formData, resource_url: e.target.value})}
                        className="w-full p-4 bg-gray-50 rounded-2xl font-bold border-2 border-transparent focus:border-indigo-500 outline-none"
                      />
                    </section>

                    {/* SUBMISSION REQUIREMENTS */}
                    <section className="space-y-4 pt-8 border-t border-gray-100">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-orange-50 text-orange-600 rounded-xl shadow-sm"><SafeIcon name="Video" className="w-5 h-5" /></div>
                        <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Submission Criteria</h4>
                      </div>
                      
                      <label className="flex items-center gap-4 bg-gray-50 p-6 rounded-[2rem] border border-gray-100 shadow-inner cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={formData.require_video_submission} 
                          onChange={e => setFormData({...formData, require_video_submission: e.target.checked})}
                          className="w-6 h-6 rounded-lg text-indigo-600 border-gray-300"
                        />
                        <div>
                          <span className="block font-black text-gray-900">Require Video Submission</span>
                          <span className="block text-xs text-gray-500 font-medium">Owner must upload a video of this task to complete it.</span>
                        </div>
                      </label>

                      {formData.require_video_submission && (
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Submission Instructions</label>
                          <RichTextEditor 
                            value={formData.video_submission_description} 
                            onChange={val => setFormData({...formData, video_submission_description: val})} 
                            placeholder="Tell the owner what to film..."
                          />
                        </div>
                      )}
                    </section>
                  </>
                )}
              </form>

              <div className="p-8 border-t bg-gray-50 flex gap-4">
                <button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-[2] bg-indigo-600 text-white py-5 rounded-3xl font-black shadow-2xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center justify-center gap-3"
                >
                  {isSubmitting ? <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" /> : <><SafeIcon name="Check" className="w-6 h-6" /> Confirm & Save Task</>}
                </button>
                <button onClick={() => setShowCreateForm(false)} className="flex-1 bg-white border-2 border-gray-100 py-5 rounded-3xl font-black text-gray-500 hover:bg-gray-100 transition-all">Dismiss</button>
              </div>

              {/* TEMPLATE PICKER OVERLAY */}
              <AnimatePresence>
                {showTemplatePicker && (
                  <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="absolute inset-y-0 right-0 w-80 bg-white border-l shadow-2xl z-50 flex flex-col"
                  >
                    <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
                      <h4 className="font-black text-gray-900">Task Templates</h4>
                      <button onClick={() => setShowTemplatePicker(false)}><SafeIcon name="X" className="w-5 h-5 text-gray-400" /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                      {templates.map(t => (
                        <button 
                          key={t.id}
                          onClick={() => handleApplyTemplate(t)}
                          className="w-full text-left p-4 rounded-2xl border-2 border-gray-50 hover:border-indigo-500 hover:bg-indigo-50 transition-all"
                        >
                          <p className="font-black text-gray-900 text-sm">{t.title}</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">{t.category}</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TasksSection;